import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '../../hooks';
import { isRowPress } from './rowPress';

/**
 * ResponsiveTable (CO-150): the one pattern for a history or a list of
 * records — deployments, runs on the server, database previews, sites.
 *
 * - A table with every column when the table itself is at least 960 px
 *   wide (a container query, so the sidebar or a panel's padding never
 *   squeezes it).
 * - Narrower: columns marked `secondary` (the server, who started it)
 *   give way, so the essential ones and the row's action fit without
 *   sideways scrolling. A cell can keep a secondary fact visible there
 *   with `<Fold>`, which shows only while its column is hidden.
 * - Below 768 px of viewport: each row is a card in reading order —
 *   title, status and the facts with their labels.
 * - A row that has a page of its own (`rowLink`) opens it when pressed
 *   anywhere, in the table and on a card; there is no "View" button. A link
 *   named for the row stays in its first cell for keyboards and screen
 *   readers, and buttons, links and fields inside a row keep their own
 *   behaviour.
 *
 * Widths: a number is a share of the table; "status", "time" and "code"
 * are fixed widths that always hold a badge, a "21 min ago / 58 s" pair or
 * a 10-character commit without breaking a word; "fill" takes the rest.
 *
 * Exactly one of the table and the cards is rendered, so assistive
 * technology never reads a record twice.
 */
export interface Column<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** "secondary" columns give way in a table narrower than 960 px. */
  priority?: 'primary' | 'secondary';
  /** Where the value goes on a card: its title line, its status corner,
   * the labelled facts (default) or nowhere. */
  card?: 'title' | 'status' | 'fact' | 'none';
  /** The fact's label on a card (defaults to the header). */
  cardLabel?: string;
  /** A share of the table in percent, a fixed width for a badge, a time
   * or a commit, or "fill" for the rest. */
  width?: 8 | 10 | 12 | 14 | 16 | 20 | 24 | 28 | 'status' | 'time' | 'code' | 'fill';
}

/** A secondary fact shown inside another cell only while its own column is hidden. */
export function Fold({ children }: { children: ReactNode }) {
  return <span className="rt-fold">{children}</span>;
}

export interface RowLink {
  to: string;
  /** A name that says which row: "Open the d81850c deployment". */
  accessibleName: string;
}

/**
 * Opens the row's page on a plain press by pressing the row's own link, so
 * the router (and a table outside one) behaves as it does for any link.
 * Text being selected and presses on a control inside the row are left alone.
 */
function openRow(link: RowLink | null | undefined) {
  if (!link) return undefined;
  return (event: MouseEvent<HTMLElement>) => {
    const anchor = event.currentTarget.querySelector<HTMLAnchorElement>('a.rt-row-open');
    if (!anchor) return;
    // Command or control opens a new tab, as it does on a link.
    if (!event.defaultPrevented && event.button === 0 && (event.metaKey || event.ctrlKey) && !(event.target as HTMLElement).closest('a, button, input, select, textarea, label, summary')) {
      window.open(anchor.href, '_blank', 'noopener');
      return;
    }
    if (isRowPress(event)) anchor.click();
  };
}

export function ResponsiveTable<T>({
  rows,
  columns,
  rowKey,
  caption,
  rowLink,
  actions,
  actionsSize = 'md',
  detail,
  rowProps,
  testId,
  className,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  /** Names the table (visually hidden) and the card list. */
  caption: string;
  /** The page a row opens when pressed (CO-185); null for a row without one. */
  rowLink?: (row: T) => RowLink | null;
  /** Buttons and menus per row instead of one link (CO-158): the table's
   * last column, the foot of a card on a phone. */
  actions?: (row: T) => ReactNode;
  /** The width the actions need: md fits a button and a menu, lg a long
   * button label and a menu. */
  actionsSize?: 'md' | 'lg';
  /** A row's details, shown under it while the caller says it is open.
   * The caller renders the toggle (aria-expanded) in a cell or action. */
  detail?: (row: T) => ReactNode | null;
  rowProps?: (row: T) => { testId?: string; highlighted?: boolean };
  testId?: string;
  className?: string;
}) {
  const phone = useMediaQuery('(max-width: 767.98px)');
  // Below 960 px of table width the secondary columns are hidden by CSS. A
  // detail row spanning every column would then add phantom columns, and a
  // fixed-layout table shares its free width with them: the fill column
  // collapses. So the detail spans only the columns on show.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = useState(false);
  useLayoutEffect(() => {
    const element = wrapRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setNarrow(element.clientWidth < 960);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [phone]);
  if (phone) {
    return (
      <ul className={`rt-cards${className ? ` ${className}` : ''}`} aria-label={caption} data-testid={testId}>
        {rows.map((row) => {
          const extra = rowProps?.(row);
          const title = columns.filter((column) => column.card === 'title');
          const status = columns.filter((column) => column.card === 'status');
          const facts = columns.filter((column) => (column.card ?? 'fact') === 'fact');
          const link = rowLink?.(row);
          const rowActions = actions?.(row);
          const rowDetail = detail?.(row);
          return (
            <li key={rowKey(row)} className={`rt-card${extra?.highlighted ? ' rt-card--highlighted' : ''}${link ? ' rt-card--link' : ''}`} data-testid={extra?.testId} onClick={openRow(link)}>
              <div className="rt-card__head">
                <div className="rt-card__title">
                  {title.map((column) => (
                    <div key={column.id}>{column.cell(row)}</div>
                  ))}
                  {link ? <RowOpenLink link={link} /> : null}
                </div>
                {status.length > 0 ? (
                  <div className="rt-card__status">
                    {status.map((column) => (
                      <div key={column.id}>{column.cell(row)}</div>
                    ))}
                  </div>
                ) : null}
              </div>
              {facts.length > 0 ? (
                <dl className="rt-card__facts">
                  {facts.map((column) => (
                    <div key={column.id} className="rt-card__fact">
                      <dt>{column.cardLabel ?? column.header}</dt>
                      <dd>{column.cell(row)}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {rowDetail ? <div className="rt-card__detail">{rowDetail}</div> : null}
              {rowActions ? <div className="rt-card__actions">{rowActions}</div> : null}
            </li>
          );
        })}
      </ul>
    );
  }
  const actionClass = `rt-col rt-col--action rt-col--actions rt-col--actions-${actionsSize}`;
  const hasActionColumn = Boolean(actions);
  const shown = narrow ? columns.filter((column) => column.priority !== 'secondary') : columns;
  const span = shown.length + (hasActionColumn ? 1 : 0);
  return (
    <div className={`rt-table-wrap${className ? ` ${className}` : ''}`} data-testid={testId} ref={wrapRef}>
      <table className="rt-table">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.id} scope="col" className={columnClass(column)}>
                {column.header}
              </th>
            ))}
            {hasActionColumn ? (
              <th scope="col" className={actionClass}>
                <span className="visually-hidden">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const extra = rowProps?.(row);
            const link = rowLink?.(row);
            const rowDetail = detail?.(row);
            return (
              <Fragment key={rowKey(row)}>
              <tr data-testid={extra?.testId} onClick={openRow(link)} className={`${extra?.highlighted ? 'rt-row--highlighted' : ''}${rowDetail ? ' rt-row--open' : ''}${link ? ' rt-row--link' : ''}`.trim() || undefined}>
                {columns.map((column, index) =>
                  // The first column names the row, so it is the row's header.
                  index === 0 ? (
                    <th key={column.id} scope="row" className={columnClass(column)}>
                      {column.cell(row)}
                      {link ? <RowOpenLink link={link} /> : null}
                    </th>
                  ) : (
                    <td key={column.id} className={columnClass(column)}>
                      {column.cell(row)}
                    </td>
                  ),
                )}
                {hasActionColumn ? (
                  <td className={actionClass}>
                    {actions ? <span className="rt-actions">{actions(row)}</span> : null}
                  </td>
                ) : null}
              </tr>
              {rowDetail ? (
                <tr className="rt-detail">
                  <td colSpan={span}>{rowDetail}</td>
                </tr>
              ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The row's link for keyboards and screen readers; pointers press the row itself. */
function RowOpenLink({ link }: { link: RowLink }) {
  return (
    <Link className="visually-hidden rt-row-open" to={link.to} aria-label={link.accessibleName} />
  );
}

function columnClass<T>(column: Column<T>): string {
  return `rt-col rt-col--${column.id}${column.width ? ` rt-col--w-${column.width}` : ''}${column.priority === 'secondary' ? ' rt-col--secondary' : ''}`;
}
